import { createTank, generateArena } from "./arena";
import {
  GAME_CONSTANTS,
  PLAYER_IDS,
  otherPlayer,
  type Arena,
  type ComputerDifficulty,
  type DifficultySelection,
  type GameEvent,
  type GameMode,
  type GameState,
  type FlagState,
  type InputState,
  type PlayerId,
  type Players,
  type ProjectileState,
  type TankState,
  type Vec2,
  type Wall,
  type SinglePlayerMode,
} from "./types";
import { createComputerState } from "./computer";

const EPSILON = 0.000_001;

function normalizeAngle(angle: number): number {
  let value = angle % (Math.PI * 2);
  if (value < -Math.PI) value += Math.PI * 2;
  if (value > Math.PI) value -= Math.PI * 2;
  return value;
}

function circleIntersectsWall(
  x: number,
  y: number,
  radius: number,
  wall: Wall,
): boolean {
  const nearestX = Math.max(wall.x, Math.min(x, wall.x + wall.width));
  const nearestY = Math.max(wall.y, Math.min(y, wall.y + wall.height));
  const dx = x - nearestX;
  const dy = y - nearestY;
  return dx * dx + dy * dy < radius * radius;
}

function validTankPosition(
  x: number,
  y: number,
  arena: Arena,
  otherTank?: TankState,
): boolean {
  const radius = GAME_CONSTANTS.tankRadius;
  if (
    x < radius ||
    y < radius ||
    x > arena.width - radius ||
    y > arena.height - radius
  ) {
    return false;
  }
  if (arena.walls.some((wall) => circleIntersectsWall(x, y, radius, wall))) {
    return false;
  }
  if (otherTank) {
    const dx = x - otherTank.x;
    const dy = y - otherTank.y;
    if (dx * dx + dy * dy < (radius * 2) ** 2) return false;
  }
  return true;
}

export function advanceTank(
  tank: TankState,
  input: InputState,
  arena: Arena,
  otherTank: TankState,
  deltaSeconds: number,
  now = Number.POSITIVE_INFINITY,
): void {
  if (now < tank.immobilizedUntil) return;

  // Navigation is absolute to the arena: north is -Y, south is +Y, west is
  // -X, and east is +X. The tank faces its last movement direction so shots
  // remain predictable without requiring a separate turret control.
  const fallbackX = Number(input.right) - Number(input.left);
  const fallbackY = Number(input.backward) - Number(input.forward);
  const directionX = Math.abs(input.axisX) > EPSILON ? input.axisX : fallbackX;
  const directionY = Math.abs(input.axisY) > EPSILON ? input.axisY : fallbackY;
  if (directionX === 0 && directionY === 0) return;
  const length = Math.hypot(directionX, directionY);
  const magnitude = Math.min(1, Math.hypot(directionX, directionY));
  const speed = directionY > 0 && Math.abs(directionX) < 0.001
    ? GAME_CONSTANTS.reverseSpeed
    : GAME_CONSTANTS.forwardSpeed;
  const distance = speed * magnitude * deltaSeconds;
  const dx = (directionX / length) * distance;
  const dy = (directionY / length) * distance;
  tank.angle = normalizeAngle(Math.atan2(dy, dx));
  const nextX = tank.x + dx;
  const nextY = tank.y + dy;

  if (validTankPosition(nextX, nextY, arena, otherTank)) {
    tank.x = nextX;
    tank.y = nextY;
    return;
  }
  if (validTankPosition(nextX, tank.y, arena, otherTank)) tank.x = nextX;
  if (validTankPosition(tank.x, nextY, arena, otherTank)) tank.y = nextY;
}

function segmentAabbTime(
  x: number,
  y: number,
  dx: number,
  dy: number,
  wall: Wall,
  radius: number,
): number | null {
  const minX = wall.x - radius;
  const maxX = wall.x + wall.width + radius;
  const minY = wall.y - radius;
  const maxY = wall.y + wall.height + radius;
  let near = 0;
  let far = 1;

  const axes: readonly [number, number, number, number][] = [
    [x, dx, minX, maxX],
    [y, dy, minY, maxY],
  ];
  for (const [origin, direction, min, max] of axes) {
    if (Math.abs(direction) < EPSILON) {
      if (origin < min || origin > max) return null;
      continue;
    }
    const first = (min - origin) / direction;
    const second = (max - origin) / direction;
    near = Math.max(near, Math.min(first, second));
    far = Math.min(far, Math.max(first, second));
    if (near > far) return null;
  }
  return near >= 0 && near <= 1 ? near : null;
}

function segmentCircleTime(
  x: number,
  y: number,
  dx: number,
  dy: number,
  target: TankState,
  radius: number,
): number | null {
  const offsetX = x - target.x;
  const offsetY = y - target.y;
  const a = dx * dx + dy * dy;
  if (a < EPSILON) return null;
  const b = 2 * (offsetX * dx + offsetY * dy);
  const c = offsetX * offsetX + offsetY * offsetY - radius * radius;
  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;
  const root = Math.sqrt(discriminant);
  const first = (-b - root) / (2 * a);
  if (first >= 0 && first <= 1) return first;
  const second = (-b + root) / (2 * a);
  return second >= 0 && second <= 1 ? second : null;
}

function boundaryExitTime(
  projectile: ProjectileState,
  dx: number,
  dy: number,
  arena: Arena,
): number | null {
  const radius = GAME_CONSTANTS.projectileRadius;
  const endX = projectile.x + dx;
  const endY = projectile.y + dy;
  if (
    endX >= radius &&
    endX <= arena.width - radius &&
    endY >= radius &&
    endY <= arena.height - radius
  ) {
    return null;
  }
  const candidates: number[] = [];
  if (dx < 0) candidates.push((radius - projectile.x) / dx);
  if (dx > 0) candidates.push((arena.width - radius - projectile.x) / dx);
  if (dy < 0) candidates.push((radius - projectile.y) / dy);
  if (dy > 0) candidates.push((arena.height - radius - projectile.y) / dy);
  const valid = candidates.filter((value) => value >= 0 && value <= 1);
  return valid.length > 0 ? Math.min(...valid) : 0;
}

function spawnProjectile(tank: TankState): ProjectileState {
  const muzzle = GAME_CONSTANTS.tankRadius + 13;
  return {
    owner: tank.playerId,
    x: tank.x + Math.cos(tank.angle) * muzzle,
    y: tank.y + Math.sin(tank.angle) * muzzle,
    vx: Math.cos(tank.angle) * GAME_CONSTANTS.projectileSpeed,
    vy: Math.sin(tank.angle) * GAME_CONSTANTS.projectileSpeed,
  };
}

function createFlags(arena: Arena): Players<FlagState> {
  return {
    1: {
      owner: 1,
      x: arena.spawns[1].x,
      y: arena.spawns[1].y,
      carrier: null,
    },
    2: {
      owner: 2,
      x: arena.spawns[2].x,
      y: arena.spawns[2].y,
      carrier: null,
    },
  };
}

function distanceBetween(first: Vec2, second: Vec2): number {
  return Math.hypot(first.x - second.x, first.y - second.y);
}

function syncCarriedFlags(state: GameState): void {
  if (!state.flags) return;
  for (const flagOwner of PLAYER_IDS) {
    const flag = state.flags[flagOwner];
    if (flag.carrier !== null) {
      flag.x = state.tanks[flag.carrier].x;
      flag.y = state.tanks[flag.carrier].y;
    }
  }
}

function dropCarriedFlags(
  state: GameState,
  playerId: PlayerId,
  events: GameEvent[],
): boolean {
  if (!state.flags) return false;
  const tank = state.tanks[playerId];
  const home = state.arena.spawns[playerId];
  const awayX = tank.x - home.x;
  const awayY = tank.y - home.y;
  const awayDistance = Math.hypot(awayX, awayY);
  const directionX = awayDistance > EPSILON
    ? awayX / awayDistance
    : playerId === 1
      ? 1
      : -1;
  const directionY = awayDistance > EPSILON ? awayY / awayDistance : 0;
  let dropped = false;
  for (const flagOwner of PLAYER_IDS) {
    const flag = state.flags[flagOwner];
    if (flag.carrier !== playerId) continue;
    flag.carrier = null;
    dropped = true;
    flag.x = Math.max(
      GAME_CONSTANTS.flagRadius,
      Math.min(
        state.arena.width - GAME_CONSTANTS.flagRadius,
        tank.x + directionX * GAME_CONSTANTS.flagDropDistance,
      ),
    );
    flag.y = Math.max(
      GAME_CONSTANTS.flagRadius,
      Math.min(
        state.arena.height - GAME_CONSTANTS.flagRadius,
        tank.y + directionY * GAME_CONSTANTS.flagDropDistance,
      ),
    );
    events.push({ type: "flag-dropped", playerId, flagOwner: flag.owner });
  }
  return dropped;
}

function updateCaptureTheFlag(state: GameState, events: GameEvent[]): boolean {
  if (!state.flags) return false;
  syncCarriedFlags(state);

  for (const playerId of PLAYER_IDS) {
    const tank = state.tanks[playerId];
    const ownFlag = state.flags[playerId];
    const home = state.arena.spawns[playerId];
    if (
      ownFlag.carrier === null &&
      distanceBetween(tank, ownFlag) <=
        GAME_CONSTANTS.tankRadius + GAME_CONSTANTS.flagRadius &&
      distanceBetween(ownFlag, home) > 1
    ) {
      ownFlag.x = home.x;
      ownFlag.y = home.y;
      events.push({ type: "flag-returned", playerId, flagOwner: playerId });
    }

    const enemyId = otherPlayer(playerId);
    const enemyFlag = state.flags[enemyId];
    if (
      enemyFlag.carrier === null &&
      distanceBetween(tank, enemyFlag) <=
        GAME_CONSTANTS.tankRadius + GAME_CONSTANTS.flagRadius
    ) {
      enemyFlag.carrier = playerId;
      enemyFlag.x = tank.x;
      enemyFlag.y = tank.y;
      events.push({
        type: "flag-picked-up",
        playerId,
        flagOwner: enemyId,
      });
    }

    if (enemyFlag.carrier === playerId) {
      enemyFlag.x = tank.x;
      enemyFlag.y = tank.y;
      if (distanceBetween(tank, home) <= GAME_CONSTANTS.flagCaptureRadius) {
        state.phase = "gameover";
        state.winner = playerId;
        state.winReason = "capture";
        state.countdownEndsAt = null;
        state.resumePhase = null;
        state.projectiles = { 1: null, 2: null };
        events.push({ type: "gameover", winner: playerId, reason: "capture" });
        return true;
      }
    }
  }
  return false;
}

export function createGameState(
  gameId: string,
  seed: string,
  now: number,
  round = 1,
  mode: GameMode = "classic",
  computerDifficulty: DifficultySelection = "beginner",
  initialAutomaticLevel: ComputerDifficulty = "beginner",
  singlePlayerMode: SinglePlayerMode = "last-tank-standing",
): GameState {
  const arena = generateArena(seed);
  const tanks: Players<TankState> = {
    1: createTank(1, arena),
    2: createTank(2, arena),
  };
  const captureTheFlag =
    mode === "capture-the-flag" ||
    (mode === "single-player" && singlePlayerMode === "capture-the-flag");
  if (captureTheFlag) {
    tanks[1].lives = null;
    tanks[2].lives = null;
  }
  return {
    version: 1,
    gameId,
    mode,
    singlePlayerMode: mode === "single-player" ? singlePlayerMode : null,
    round,
    tick: 0,
    phase: "waiting",
    resumePhase: null,
    serverTime: now,
    countdownEndsAt: null,
    winner: null,
    winReason: null,
    arena,
    tanks,
    projectiles: { 1: null, 2: null },
    flags: captureTheFlag ? createFlags(arena) : null,
    computer:
      mode === "single-player"
        ? createComputerState(
            computerDifficulty,
            now,
            initialAutomaticLevel,
          )
        : null,
    connected: { 1: false, 2: mode === "single-player" },
    rematchReady: { 1: false, 2: false },
  };
}

export function isInputActive(input: InputState): boolean {
  return input.forward || input.backward || input.left || input.right;
}

export function stepSimulation(
  state: GameState,
  inputs: Players<InputState>,
  pendingFire: Players<boolean>,
  deltaSeconds: number,
  now: number,
): GameEvent[] {
  const events: GameEvent[] = [];
  state.serverTime = now;
  state.tick += 1;
  if (state.phase !== "playing") return events;

  for (const playerId of PLAYER_IDS) {
    advanceTank(
      state.tanks[playerId],
      inputs[playerId],
      state.arena,
      state.tanks[otherPlayer(playerId)],
      deltaSeconds,
      now,
    );
  }

  if (updateCaptureTheFlag(state, events)) return events;

  for (const playerId of PLAYER_IDS) {
    if (pendingFire[playerId] && state.projectiles[playerId] === null) {
      state.projectiles[playerId] = spawnProjectile(state.tanks[playerId]);
      events.push({ type: "shot", playerId });
    }
    pendingFire[playerId] = false;
  }

  for (const playerId of PLAYER_IDS) {
    const projectile = state.projectiles[playerId];
    if (!projectile) continue;
    const dx = projectile.vx * deltaSeconds;
    const dy = projectile.vy * deltaSeconds;
    let collisionTime = boundaryExitTime(projectile, dx, dy, state.arena);
    let collision: "wall" | "tank" | null =
      collisionTime === null ? null : "wall";

    for (const wall of state.arena.walls) {
      const time = segmentAabbTime(
        projectile.x,
        projectile.y,
        dx,
        dy,
        wall,
        GAME_CONSTANTS.projectileRadius,
      );
      if (time !== null && (collisionTime === null || time < collisionTime)) {
        collisionTime = time;
        collision = "wall";
      }
    }

    const victimId = otherPlayer(playerId);
    const victim = state.tanks[victimId];
    const tankTime = segmentCircleTime(
      projectile.x,
      projectile.y,
      dx,
      dy,
      victim,
      GAME_CONSTANTS.tankRadius + GAME_CONSTANTS.projectileRadius,
    );
    if (
      tankTime !== null &&
      (collisionTime === null || tankTime <= collisionTime)
    ) {
      collisionTime = tankTime;
      collision = "tank";
    }

    if (collision === null || collisionTime === null) {
      projectile.x += dx;
      projectile.y += dy;
      continue;
    }

    projectile.x += dx * collisionTime;
    projectile.y += dy * collisionTime;
    state.projectiles[playerId] = null;
    if (collision === "wall") {
      events.push({ type: "blocked", playerId });
      continue;
    }
    if (now < victim.invulnerableUntil) continue;

    const carriedFlag = dropCarriedFlags(state, victimId, events);
    victim.invulnerableUntil = now + GAME_CONSTANTS.invulnerabilityMs;
    victim.immobilizedUntil = carriedFlag
      ? victim.invulnerableUntil
      : now;
    if (victim.lives === null) {
      events.push({
        type: "impact",
        playerId: victimId,
        lives: null,
        invulnerableUntil: victim.invulnerableUntil,
      });
      continue;
    }
    victim.lives -= 1;
    events.push({
      type: "impact",
      playerId: victimId,
      lives: victim.lives,
      invulnerableUntil: victim.invulnerableUntil,
    });
    if (victim.lives <= 0) {
      state.phase = "gameover";
      state.winner = playerId;
      state.winReason = "elimination";
      state.countdownEndsAt = null;
      state.resumePhase = null;
      state.projectiles = { 1: null, 2: null };
      events.push({ type: "gameover", winner: playerId, reason: "elimination" });
      break;
    }
  }
  return events;
}
